import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  selectCreditControlStatusSummaries,
  selectLoadingCreditControlStatusSummaries,
  getCreditControlStatusSummaries,
} from "./CreditControlStatusSummariesTableSlice";
import { useSelector } from "react-redux";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { useEffect } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { AccountingBasisTypes, CreditControlStatusSummaryModel } from "services/api/cplapi/CPLAPI";
import CPLTable from "cpl-react-common/components/Tables/CPLTable";
import { PageDescription } from "_metronic/layout/core";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMoneyBillTrendUp } from "@fortawesome/pro-solid-svg-icons";
import translations from "utils/translationUtility";

const CreditControlStatusSummariesTable: React.FC = () => {
  const { developmentID } = useParams();
  const thunkDispatch = useThunkDispatch();
  const navigate = useNavigate();
  const creditControlsStatuses = useSelector(selectCreditControlStatusSummaries);
  const loadingCreditControlStatuses = useSelector(selectLoadingCreditControlStatusSummaries);

  const columnHelper = createColumnHelper<CreditControlStatusSummaryModel>();
  const columnDefinition = [
    columnHelper.display({
      id: "icon",
      cell: () => (
        <div className="d-flex">
          <FontAwesomeIcon icon={faMoneyBillTrendUp} size="lg" className="cpl-brand-primary" />
        </div>
      ),
      enableHiding: false,
    }),
    columnHelper.accessor((row) => row.statusTypeID, {
      id: "Status Type",
      cell: (info) => info.row.original.statusType,
      header: () => <>Status Type</>,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.numberOfClients, {
      id: "Number Of Clients",
      cell: (info) => info.getValue(),
      header: () => <>Number Of {translations.client}s</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
        dataClassName: "text-end showTableCellOnTabletAndDesktop",
      },
    }),
    columnHelper.accessor((row) => row.outstandingBalance, {
      id: "Outstanding Balance",
      cell: (info) => info.getValue()!.toGBPString(),
      header: () => <>Development Balance</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "currency-column-header",
        dataClassName: "text-end",
      },
    }),
    columnHelper.display({
      id: "actions",
      cell: (info) => {
        const isServiceChargeAccountancy =
          creditControlsStatuses.accountingBasisType === AccountingBasisTypes.ServiceCharge;

        return (
          info.row.original.showClientDetails &&
          !isServiceChargeAccountancy && (
            <div
              data-test-id={`credit-control-statuses-table__view-breakdown-link-${info.row.original.statusTypeID}`}
              className="cpl-btn cpl-btn-outline"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="View Breakdown"
              onClick={() => {
                redirectToUnit(info.row.original);
              }}
            >
              View Breakdown
            </div>
          )
        );
      },
    }),
  ];

  useEffect(() => {
    thunkDispatch(getCreditControlStatusSummaries({ developmentID: developmentID! }));
  }, [developmentID, thunkDispatch]);

  const redirectToUnit = (creditControlStatus: CreditControlStatusSummaryModel) => {
    if (!creditControlStatus.showClientDetails) return;
    const path = `/CreditControl/${developmentID}/${creditControlStatus.statusTypeID}`;
    navigate(path);
  };

  return (
    <>
      <PageDescription>{creditControlsStatuses.developmentName}</PageDescription>
      <div className="card d-flex mt-4 cpl-table-container">
        <ActivityIndicator isBusy={loadingCreditControlStatuses}>
          <div className="card-body">
            <CPLTable
              data={creditControlsStatuses.statusSummaries}
              columns={columnDefinition}
              rowMobileClickAction={(row) => redirectToUnit(row)}
              classNames="cpl-table-nowrap-below-desktop"
            />
          </div>
        </ActivityIndicator>
      </div>
    </>
  );
};

export default CreditControlStatusSummariesTable;
